import React from "react";
import { Col, Row, Skeleton, Form, Select, Input } from "antd";

import { useFetchConstant } from "apis/constant";

import FormDatePicker from "components/FormDatePicker/FormDatePicker";
import FormGroupSelectionInput from "components/FormGroupSelectionInput/FormGroupSelectionInput";
import FormInput from "components/FormInput/FormInput";
import FormSelection from "components/FormSelection/FormSelection";
import FormToggle from "components/FormToggle/FormToggle";
import SectionCard from "components/SectionCard/SectionCard";
import Title from "components/Title/Title";
import FormInputNumber from "components/FormInputNumber/FormInputNumber";
import Locale from "react-intl-universal";

const DependentInfo = ({ onFieldFocus, isLoading, relationships }) => {
  return (
    <SectionCard title={Locale.get("addNewAdvisee.sectionCardTitle_1")}>
      {isLoading ? (
        <Skeleton active />
      ) : (
        <>
          <Row align="middle">
            <Col span={18} lg={20}>
              <Title type="question" marginBottom="32px">
                {Locale.get("addNewAdvisee.questionTitle_1")}
              </Title>
            </Col>
            <Col span={6} lg={4}>
              {/* <FormInput
                name={["familyMember", "yearsOfResponsible"]}
                placeholder="20"
                onFocus={onFieldFocus}
                requiredErrorMessage="Please enter years of responsibility."
              /> */}
              <FormInputNumber
                name={["familyMember", "yearsOfResponsible"]}
                placeholder="20"
                maxValue={99}
                minValue={0}
                onFocus={onFieldFocus}
                extraRules={[
                  {
                    required: true,
                    message: Locale.get("addNewAdvisee.errMsgYearResponsible"),
                  },
                ]}
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <FormInput
                label={Locale.get("addNewAdvisee.fName")}
                name={["familyMember", "name", "firstName"]}
                placeholder="John"
                requiredErrorMessage={Locale.get("addNewAdvisee.errMsgFName")}
                onFocus={onFieldFocus}
              />
            </Col>
            <Col span={12}>
              <FormInput
                label={Locale.get("addNewAdvisee.lName")}
                name={["familyMember", "name", "lastName"]}
                placeholder="Doe"
                requiredErrorMessage={Locale.get("addNewAdvisee.errMsgLName")}
                onFocus={onFieldFocus}
              />
            </Col>
            <Col span={12}>
              <FormSelection
                label={Locale.get("addNewAdvisee.relationship")}
                name={["familyMember", "relationship"]}
                placeholder="Child"
                requiredErrorMessage={Locale.get(
                  "addNewAdvisee.errMsgRelationship"
                )}
                selections={relationships}
                onFocus={onFieldFocus}
              />
            </Col>
            <Col span={12}>
              <FormDatePicker
                label={Locale.get("addNewAdvisee.dob")}
                name={["familyMember", "dob"]}
                placeholder="1994-01-01"
                requiredErrorMessage={Locale.get("addNewAdvisee.errMsgDob")}
                onFocus={onFieldFocus}
                isDob
              />
            </Col>
          </Row>
        </>
      )}
    </SectionCard>
  );
};

const AWPersonalInfo = ({ onFieldFocus }) => {
  const {
    isLoading: isRelationshipsLoading,
    selection: relationships,
  } = useFetchConstant("relationships");
  const {
    isLoading: isCountryCodesLoading,
    selection: countryCodes,
  } = useFetchConstant("countries", "phoneCode", "phoneCode");
  const isLoading = isRelationshipsLoading || isCountryCodesLoading;

  const {
    isLoading: isMarinatalStatusLoading,
    data: marinatalStatus,
  } = useFetchConstant("marinatalStatus");

  return (
    <>
      <Title marginBottom="24px">{Locale.get("addNewAdvisee.pageTitle")}</Title>
      <Row gutter={16}>
        <Col span={0}>
          {/* Hidden field to trace leadId */}
          <FormInput label="leadId" name={"leadId"} onFocus={onFieldFocus} />
        </Col>

        <Col span={12}>
          <FormInput
            label={Locale.get("addNewAdvisee.fName")}
            name={["name", "firstName"]}
            placeholder="John"
            requiredErrorMessage={Locale.get("addNewAdvisee.errMsg_fName")}
            onFocus={onFieldFocus}
          />
        </Col>
        <Col span={12}>
          <FormInput
            label={Locale.get("addNewAdvisee.lName")}
            name={["name", "lastName"]}
            placeholder="Doe"
            requiredErrorMessage={Locale.get("addNewAdvisee.errMsg_lName")}
            onFocus={onFieldFocus}
          />
        </Col>
        <Col span={12}>
          <FormInput
            label={Locale.get("addNewAdvisee.idPassportNo")}
            name="nric"
            placeholder={Locale.get("addNewAdvisee.idPassportNoPlaceholder")}
            requiredErrorMessage="Please enter ID / Passport No."
            onFocus={onFieldFocus}
            mandatory={false}
          />
        </Col>
        <Col span={12}>
          <FormDatePicker
            label={Locale.get("addNewAdvisee.dob")}
            name="dob"
            placeholder="1994-01-01"
            requiredErrorMessage={Locale.get("addNewAdvisee.errMsg_dob")}
            onFocus={onFieldFocus}
            isDob
          />
        </Col>

        <Col span={12}>
          <FormInput
            label={Locale.get("addNewAdvisee.occupation")}
            name="occupation"
            placeholder={Locale.get("addNewAdvisee.optional")}
            type="occupation"
          />
        </Col>

        <Col span={12}>
          <FormSelection
            label={Locale.get("addNewAdvisee.gender")}
            name="gender"
            placeholder={Locale.get("addNewAdvisee.gender")}
            mandatory={false}
            requiredErrorMessage="Please select relations to dependent."
            selections={[
              {
                label: Locale.get("addNewAdvisee.maleLabel"),
                value: "Male",
              },
              {
                label: Locale.get("addNewAdvisee.femaleLabel"),
                value: "Female",
              },
            ]}
            onFocus={onFieldFocus}
          />
        </Col>

        <Col span={12}>
          <FormGroupSelectionInput
            label={Locale.get("addNewAdvisee.contactNo")}
            inputName={["contact", "contactNumber"]}
            selectionName={["contact", "countryCode"]}
            inputPlaceholder={Locale.get("addNewAdvisee.optional")}
            inputType="number"
            selections={countryCodes}
          />
        </Col>
        <Col span={12}>
          <FormInput
            label={Locale.get("addNewAdvisee.email")}
            name="email"
            placeholder={Locale.get("addNewAdvisee.optional")}
            type="email"
          />
        </Col>

        <Col span={12}>
          <FormSelection
            name="marital_status"
            label={Locale.get("addNewAdvisee.maritalStatus")}
            placeholder={Locale.get("addNewAdvisee.maritalStatus")}
            mandatory={false}
            requiredErrorMessage="Please select relations to dependent."
            selections={[
              {
                label: Locale.get("addNewAdvisee.singleLabel"),
                value: "Single",
              },
              {
                label: Locale.get("addNewAdvisee.marriedLabel"),
                value: "Married",
              },
              {
                label: Locale.get("addNewAdvisee.divorcedLabel"),
                value: "Divorced",
              },
              {
                label: Locale.get("addNewAdvisee.widowedLabel"),
                value: "Widowed",
              },
              {
                label: Locale.get("addNewAdvisee.otherLabel"),
                value: "Other",
              },
            ]}
            onFocus={onFieldFocus}
          />
        </Col>

        <Form.Item noStyle shouldUpdate>
          {(e) => {
            const marital_status = e.getFieldValue("marital_status");

            console.log("marital_status", marital_status);

            if (marital_status === "Other") {
              return (
                <Col span={12}>
                  <FormInput
                    label={Locale.get("addNewAdvisee.mso")}
                    name="marital_status_other"
                    placeholder={Locale.get("addNewAdvisee.otherLabel")}
                    requiredErrorMessage={Locale.get("addNewAdvisee.errMsgMso")}
                  />
                </Col>
              );
            }
          }}
        </Form.Item>

        <Col span={12}>
          <FormSelection
            name="isSmoker"
            label={Locale.get("addNewAdvisee.smokerStatus")}
            placeholder={Locale.get("addNewAdvisee.smokerStatus")}
            mandatory={true}
            requiredErrorMessage={Locale.get(
              "addNewAdvisee.errMsgSmokerStatus"
            )}
            selections={[
              {
                label: Locale.get("addNewAdvisee.smokerYes"),
                value: true,
              },
              {
                label: Locale.get("addNewAdvisee.smokerNo"),
                value: false,
              },
            ]}
            onFocus={onFieldFocus}
          />
        </Col>

        <Col span={12}>
          <FormInput
            label={Locale.get("addNewAdvisee.remarks")}
            name="remarks"
            placeholder={Locale.get("addNewAdvisee.optional")}
          />
        </Col>
      </Row>
      <FormToggle
        label={Locale.get("addNewAdvisee.toggleLabel_0")}
        name="isClient"
      >
        <SectionCard title={Locale.get("addNewAdvisee.sectionCardTitle_0")}>
          {isLoading ? (
            <Skeleton active />
          ) : (
            <FormDatePicker name="joinDate" onFocus={onFieldFocus} />
          )}
        </SectionCard>
      </FormToggle>

      <FormToggle
        label={Locale.get("addNewAdvisee.toggleLabel_1")}
        name="hasDependent"
      >
        <DependentInfo
          onFieldFocus={onFieldFocus}
          isLoading={isLoading}
          relationships={relationships}
        />
      </FormToggle>
    </>
  );
};

export default AWPersonalInfo;
